import { z } from 'zod';

/**
 * Content Card as the SDKs receive it (BB-61, CARD-001): the canonical model the Android and iOS SDKs parse, render
 * natively (VCG draws its own Compose / SwiftUI feed) and report on. Additive changes only; breaking ones bump
 * `schemaVersion`.
 */
export const CARD_TYPES = ['image_only', 'captioned_image', 'classic'] as const;
export const CardType = z.enum(CARD_TYPES);
export type CardType = z.infer<typeof CardType>;

/** Total size of the content fields after templating; a bigger card is not sent (`payload_too_large`). */
export const CARD_PAYLOAD_MAX_BYTES = 2048;

const HttpsUrl = z.url({ protocol: /^https$/, error: 'must be an https:// URL' }).max(2048);
const DeepLink = z
  .string()
  .min(1)
  .max(2048)
  .regex(/^[a-z][a-z0-9+.-]*:/i, 'must be a URI with a scheme');

export const CardAction = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('none') }),
  z.strictObject({ kind: z.literal('deep_link'), uri: DeepLink }),
  z.strictObject({ kind: z.literal('web_url'), uri: HttpsUrl, openIn: z.enum(['in_app', 'external']) }),
  z.strictObject({ kind: z.literal('log_event'), name: z.string().min(1).max(255), properties: z.record(z.string(), z.unknown()).optional() }),
  z.strictObject({ kind: z.literal('log_attribute'), name: z.string().min(1).max(255), value: z.union([z.string().max(255), z.number(), z.boolean()]) }),
]);
export type CardAction = z.infer<typeof CardAction>;

/** `aspectRatio` is width / height of the image, kept as uploaded (no fixed 4:3). */
export const CardImage = z.strictObject({ url: HttpsUrl, aspectRatio: z.number().positive().max(10), alt: z.string().max(300).optional() });
export type CardImage = z.infer<typeof CardImage>;

const Text = (max: number) => z.string().min(1).max(max).optional();

const ExtraKey = z.string().min(1).max(100);
/** A typed extra (BB-58): `enum_list` is a list, `number` and `boolean` keep their type, everything else is a string. */
export const TypedExtraValue = z.union([z.string().max(1000), z.number(), z.boolean(), z.array(z.string().min(1).max(100)).max(50)]);
export type TypedExtraValue = z.infer<typeof TypedExtraValue>;

export const Card = z
  .strictObject({
    schemaVersion: z.literal(1),
    /** Card instance id (UUIDv7), new for every send. */
    id: z.uuid(),
    /** The campaign or Canvas step that sent it, and its variant. */
    messageApiId: z.uuid(),
    variantApiId: z.string().min(1).max(120),
    type: CardType,
    title: Text(500),
    description: Text(2000),
    linkText: Text(100),
    image: CardImage.optional(),
    action: CardAction,
    actionOverrides: z.strictObject({ android: CardAction.optional(), ios: CardAction.optional(), web: CardAction.optional() }).optional(),
    pinned: z.boolean(),
    dismissible: z.boolean(),
    /** Raw key-value pairs as strings (VCG: `banner-type`, `bonus-type`, `target-screen`, `bonus-campaign`), as before. */
    extras: z.record(ExtraKey, z.string().max(1000)),
    /**
     * The same extras typed by the environment's extras schema (BB-58); keys the schema does not know are only in
     * `extras`. Optional: older senders and cards without a schema leave it out.
     */
    typedExtras: z.record(ExtraKey, TypedExtraValue).optional(),
    locale: z.string().min(2).max(35).optional(),
    /** A control-group card is never shown; the SDK only logs it (BB-70). */
    isControl: z.boolean(),
    /** Read on any device of the profile (BB-69); absent until read. */
    read: z.boolean().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
  })
  .superRefine((card, ctx) => {
    if (card.pinned && card.dismissible) ctx.addIssue({ code: 'custom', path: ['dismissible'], message: 'a pinned card cannot be dismissible' });
    const need = (field: 'image' | 'title' | 'description', why: string) => {
      if (card[field] === undefined) ctx.addIssue({ code: 'custom', path: [field], message: `${card.type} needs ${why}` });
    };
    if (card.type === 'image_only') need('image', 'an image');
    if (card.type === 'captioned_image') {
      need('image', 'an image');
      need('title', 'a title');
    }
    if (card.type === 'classic' && card.title === undefined && card.description === undefined) ctx.addIssue({ code: 'custom', path: ['title'], message: 'classic needs a title or a description' });
    if (card.expiresAt <= card.createdAt) ctx.addIssue({ code: 'custom', path: ['expiresAt'], message: 'expiresAt must be after createdAt' });
    for (const key of Object.keys(card.typedExtras ?? {})) {
      if (!(key in card.extras)) ctx.addIssue({ code: 'custom', path: ['typedExtras', key], message: 'every typed extra is also a raw extra' });
    }
  });
export type Card = z.infer<typeof Card>;
